/**
 * TopologyPage - Network Topology
 *
 * Placeholder page for the network topology visualisation
 * which is currently under development.
 */

import { Box, Stack, Text, useMantineColorScheme } from '@mantine/core';

/* ====================================
   CSS Keyframe Animations (injected once)
   Premium animations for the Coming Soon card entrance.
   ==================================== */
const topoKeyframes = `
  @keyframes topoPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes topoCardFloat {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
    }
  }

  @keyframes topoPulseRing {
    0% {
      opacity: 0.6;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(2.2);
    }
  }
`;

// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
  const styleId = 'topo-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = topoKeyframes;
    document.head.appendChild(style);
  }
}

/** Network node icon for the placeholder */
function IconNetwork({ size = 48 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="5" cy="19" r="2" />
      <circle cx="19" cy="19" r="2" />
      <line x1="12" y1="7" x2="5" y2="17" />
      <line x1="12" y1="7" x2="19" y2="17" />
      <line x1="5" y1="19" x2="19" y2="19" />
    </svg>
  );
}

export function TopologyPage() {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <Stack gap="lg" style={{ animation: 'topoPageFadeIn 400ms ease-out' }}>
      {/* Page header */}
      <Box>
        <Text
          style={{
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: 'var(--text-primary)',
          }}
        >
          Network Topology
        </Text>
        <Text
          style={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'var(--text-muted)',
            marginTop: 4,
          }}
        >
          Real-time view of your radio infrastructure
        </Text>
      </Box>

      {/* Coming Soon placeholder */}
      <Box
        style={{
          height: 'calc(100vh - 220px)',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
            padding: '3rem 2rem',
            maxWidth: 440,
            textAlign: 'center',
          }}
        >
          {/* Animated icon with pulse ring */}
          <Box
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 96,
              height: 96,
              animation: 'topoCardFloat 4s ease-in-out infinite',
            }}
          >
            {/* Pulse ring */}
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: isDark
                  ? '2px solid rgba(59, 130, 246, 0.3)'
                  : '2px solid rgba(59, 130, 246, 0.2)',
                animation: 'topoPulseRing 3s ease-out infinite',
              }}
            />
            {/* Icon container */}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.03) 100%)',
                border: isDark
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : '1px solid rgba(59, 130, 246, 0.15)',
                color: '#3B82F6',
              }}
            >
              <IconNetwork size={32} />
            </Box>
          </Box>

          {/* Title */}
          <Text
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--text-primary)',
            }}
          >
            Coming Soon
          </Text>

          {/* Description */}
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: 400,
              color: 'var(--text-muted)',
              lineHeight: 1.6,
              maxWidth: 360,
            }}
          >
            Network topology visualisation is under development. This feature
            will provide a real-time interactive map of your repeater
            infrastructure, site connections, and radio presence.
          </Text>

          {/* Subtle status line */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 20,
              backgroundColor: isDark
                ? 'rgba(59, 130, 246, 0.08)'
                : 'rgba(59, 130, 246, 0.06)',
              border: isDark
                ? '1px solid rgba(59, 130, 246, 0.15)'
                : '1px solid rgba(59, 130, 246, 0.1)',
            }}
          >
            <Box
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#3B82F6',
              }}
            />
            <Text
              style={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#3B82F6',
                letterSpacing: '0.01em',
              }}
            >
              In Development
            </Text>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
